import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';

function createDefaultConfig(settingsCopy) {
  return {
    siteName: 'MyriadSkills',
    siteDesc: settingsCopy.defaultConfig.siteDesc,
    contactEmail: 'admin@myriadskills.cn',
    regEnabled: true,
    guestBrowse: true,
    autoPoints: true,
    emailNotif: false,
    maintenanceMode: false,
    welcomeEmailTemplate: settingsCopy.defaultConfig.welcomeEmailTemplate,
    passwordResetTemplate: settingsCopy.defaultConfig.passwordResetTemplate,
    notificationDigestTemplate: settingsCopy.defaultConfig.notificationDigestTemplate,
  };
}

export default function AdminSettings() {
  const { copy } = useAdminLanguage();
  const settingsCopy = copy.settings;
  const defaultConfig = useMemo(() => createDefaultConfig(settingsCopy), [settingsCopy]);
  const [config, setConfig] = useState(defaultConfig);
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('general');
  const [resetRequests, setResetRequests] = useState([]);
  const [resetLoading, setResetLoading] = useState(false);
  const [operating, setOperating] = useState('');
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsTest, setSmsTest] = useState({
    to: '',
    message: settingsCopy.defaultConfig.smsTestMessage,
  });
  const [smsLoading, setSmsLoading] = useState(false);
  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/settings')
      .then(res => setConfig({ ...defaultConfig, ...(res.data.config || {}) }))
      .finally(() => setLoading(false));
  }, [defaultConfig]);

  const save = async () => {
    const res = await api.put('/admin/settings', { config });
    setConfig({ ...defaultConfig, ...(res.data.config || {}) });
    setSaved(settingsCopy.saved);
    setTimeout(() => setSaved(''), 2500);
  };

  const update = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  const loadNotificationTemplates = async () => {
    setTemplateLoading(true);
    try {
      const res = await api.get('/admin/notification-templates');
      setNotificationTemplates(res.data.templates || []);
    } finally {
      setTemplateLoading(false);
    }
  };

  const updateNotificationTemplate = (templateKey, locale, field, value) => {
    setNotificationTemplates(list => list.map(template => {
      if (template.key !== templateKey) return template;
      return {
        ...template,
        locales: {
          ...template.locales,
          [locale]: {
            ...template.locales[locale],
            [field]: value,
          },
        },
      };
    }));
  };

  const saveNotificationTemplates = async () => {
    const overrides = {};
    notificationTemplates.forEach(template => {
      overrides[template.key] = {
        zh: {
          title: template.locales.zh.title,
          content: template.locales.zh.content,
        },
        en: {
          title: template.locales.en.title,
          content: template.locales.en.content,
        },
      };
    });
    const res = await api.put('/admin/notification-templates', { overrides });
    setNotificationTemplates(res.data.templates || []);
    setSaved(res.data.message || settingsCopy.notifications.saved);
    setTimeout(() => setSaved(''), 2500);
  };

  const loadSmsStatus = async () => {
    setSmsLoading(true);
    try {
      const res = await api.get('/admin/sms/status');
      setSmsStatus(res.data || null);
    } finally {
      setSmsLoading(false);
    }
  };

  const sendSmsTest = async () => {
    setSmsLoading(true);
    try {
      const res = await api.post('/admin/sms/test', smsTest);
      setSaved(res.data.message || settingsCopy.email.smsSubmitted);
      setTimeout(() => setSaved(''), 2500);
    } catch (err) {
      setSaved(err.response?.data?.error || settingsCopy.email.smsFailed);
      setTimeout(() => setSaved(''), 2500);
    } finally {
      setSmsLoading(false);
    }
  };

  const loadResetRequests = async () => {
    setResetLoading(true);
    try {
      const res = await api.get('/admin/password-resets?status=all');
      setResetRequests(res.data.requests || []);
    } finally {
      setResetLoading(false);
    }
  };

  const resolveResetRequest = async (id, status) => {
    await api.post(`/admin/password-resets/${id}/resolve`, { status });
    await loadResetRequests();
    setSaved(status === 'ignored' ? settingsCopy.email.ignoredSaved : settingsCopy.email.resolvedSaved);
    setTimeout(() => setSaved(''), 2500);
  };

  const downloadJson = (payload, filename) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const runMaintenance = async (type) => {
    setOperating(type);
    try {
      if (type === 'export') {
        const res = await api.get('/admin/maintenance/export');
        downloadJson(res.data, `skill-platform-backup-${Date.now()}.json`);
        setSaved(settingsCopy.system.backupGenerated);
      } else if (type === 'logs') {
        const res = await api.get('/admin/maintenance/audit-logs');
        downloadJson(res.data, `skill-platform-audit-${Date.now()}.json`);
        setSaved(settingsCopy.system.logsExported);
      } else if (type === 'cache') {
        const res = await api.post('/admin/maintenance/clear-cache');
        setSaved(res.data.message || settingsCopy.system.cacheRefreshed);
      }
      setTimeout(() => setSaved(''), 2500);
    } finally {
      setOperating('');
    }
  };

  return (
    <div>
      <div className="page-title">{settingsCopy.title}</div>
      {loading && <div className="card">{settingsCopy.loading}</div>}

      {!loading && <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16 }}>
        <div className="card" style={{ padding: 12, height: 'fit-content' }}>
          <button className={`btn btn-sm ${tab === 'general' ? 'btn-primary' : 'btn-outline'}`} style={{ display: 'block', width: '100%', marginBottom: 6, textAlign: 'left' }} onClick={() => setTab('general')}>{settingsCopy.tabs.general}</button>
          <button className={`btn btn-sm ${tab === 'policy' ? 'btn-primary' : 'btn-outline'}`} style={{ display: 'block', width: '100%', marginBottom: 6, textAlign: 'left' }} onClick={() => setTab('policy')}>{settingsCopy.tabs.policy}</button>
          <button className={`btn btn-sm ${tab === 'email' ? 'btn-primary' : 'btn-outline'}`} style={{ display: 'block', width: '100%', marginBottom: 6, textAlign: 'left' }} onClick={() => { setTab('email'); loadResetRequests(); loadSmsStatus(); }}>{settingsCopy.tabs.email}</button>
          <button className={`btn btn-sm ${tab === 'notifications' ? 'btn-primary' : 'btn-outline'}`} style={{ display: 'block', width: '100%', marginBottom: 6, textAlign: 'left' }} onClick={() => { setTab('notifications'); loadNotificationTemplates(); }}>{settingsCopy.tabs.notifications}</button>
          <button className={`btn btn-sm ${tab === 'system' ? 'btn-primary' : 'btn-outline'}`} style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => setTab('system')}>{settingsCopy.tabs.system}</button>
        </div>

        <div className="card">
          {saved && <div style={{ background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 6, marginBottom: 14 }}>✓ {saved}</div>}

          {tab === 'general' && (
            <>
              <h3 style={{ marginBottom: 14 }}>{settingsCopy.general.title}</h3>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.general.siteName}</label>
                <input value={config.siteName} onChange={e => update('siteName', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.general.siteDesc}</label>
                <textarea value={config.siteDesc} onChange={e => update('siteDesc', e.target.value)} rows="3"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.general.contactEmail}</label>
                <input type="email" value={config.contactEmail} onChange={e => update('contactEmail', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <button className="btn btn-primary" onClick={save}>{settingsCopy.save}</button>
            </>
          )}

          {tab === 'policy' && (
            <>
              <h3 style={{ marginBottom: 14 }}>{settingsCopy.policy.title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{settingsCopy.policy.regEnabledTitle}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{settingsCopy.policy.regEnabledDesc}</div>
                </div>
                <input type="checkbox" checked={config.regEnabled} onChange={e => update('regEnabled', e.target.checked)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{settingsCopy.policy.guestBrowseTitle}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{settingsCopy.policy.guestBrowseDesc}</div>
                </div>
                <input type="checkbox" checked={config.guestBrowse} onChange={e => update('guestBrowse', e.target.checked)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{settingsCopy.policy.autoPointsTitle}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{settingsCopy.policy.autoPointsDesc}</div>
                </div>
                <input type="checkbox" checked={config.autoPoints} onChange={e => update('autoPoints', e.target.checked)} />
              </div>
              <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={save}>{settingsCopy.save}</button>
            </>
          )}

          {tab === 'email' && (
            <>
              <h3 style={{ marginBottom: 14 }}>{settingsCopy.email.title}</h3>
              <p style={{ color: '#666', fontSize: 13, marginBottom: 14 }}>{settingsCopy.email.description}</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{settingsCopy.email.emailNotifTitle}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{settingsCopy.email.emailNotifDesc}</div>
                </div>
                <input type="checkbox" checked={config.emailNotif} onChange={e => update('emailNotif', e.target.checked)} />
              </div>
              <div style={{ padding: '16px 0' }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.email.welcomeTemplate}</label>
                  <textarea value={config.welcomeEmailTemplate} onChange={e => update('welcomeEmailTemplate', e.target.value)} rows="3"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.email.passwordResetTemplate}</label>
                  <textarea value={config.passwordResetTemplate} onChange={e => update('passwordResetTemplate', e.target.value)} rows="3"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.email.notificationDigestTemplate}</label>
                  <textarea value={config.notificationDigestTemplate} onChange={e => update('notificationDigestTemplate', e.target.value)} rows="3"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{settingsCopy.email.smsTestTitle}</div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                      {smsStatus
                        ? settingsCopy.email.smsStatus(smsStatus.provider, smsStatus.twilioConfigured)
                        : settingsCopy.email.smsStatusNotLoaded}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline" disabled={smsLoading} onClick={loadSmsStatus}>
                    {smsLoading ? settingsCopy.email.loading : settingsCopy.email.refreshStatus}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'start' }}>
                  <input
                    value={smsTest.to}
                    onChange={e => setSmsTest(s => ({ ...s, to: e.target.value }))}
                    placeholder="+15551234567"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                  />
                  <textarea
                    value={smsTest.message}
                    onChange={e => setSmsTest(s => ({ ...s, message: e.target.value }))}
                    rows="2"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                  <button className="btn btn-primary" disabled={smsLoading || !smsTest.to || !smsTest.message} onClick={sendSmsTest}>
                    {smsLoading ? settingsCopy.email.sending : settingsCopy.email.sendTest}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700 }}>{settingsCopy.email.resetRequestsTitle}</div>
                  <button className="btn btn-sm btn-outline" onClick={loadResetRequests}>{settingsCopy.email.refresh}</button>
                </div>
                {resetLoading && <div style={{ color: '#777', fontSize: 13 }}>{settingsCopy.email.loading}</div>}
                {!resetLoading && resetRequests.length === 0 && (
                  <div style={{ color: '#777', fontSize: 13, padding: '12px 0' }}>{settingsCopy.email.resetEmpty}</div>
                )}
                {!resetLoading && resetRequests.map(item => (
                  <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, padding: '12px 0', borderTop: '1px solid #f5f5f5' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.email}</div>
                      <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                        {settingsCopy.email.accountStatus(item.username || settingsCopy.email.unmatchedAccount, item.status, item.created_at)}
                      </div>
                    </div>
                    <div>
                      <button className="btn btn-sm btn-primary" style={{ marginRight: 6 }} disabled={item.status !== 'pending'} onClick={() => resolveResetRequest(item.id, 'resolved')}>{settingsCopy.email.resolved}</button>
                      <button className="btn btn-sm btn-outline" disabled={item.status !== 'pending'} onClick={() => resolveResetRequest(item.id, 'ignored')}>{settingsCopy.email.ignored}</button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn-primary" onClick={save}>{settingsCopy.save}</button>
            </>
          )}

          {tab === 'notifications' && (
            <>
              <h3 style={{ marginBottom: 14 }}>{settingsCopy.notifications.title}</h3>
              <p style={{ color: '#666', fontSize: 13, marginBottom: 14 }}>{settingsCopy.notifications.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#777' }}>
                  {templateLoading ? settingsCopy.notifications.loading : settingsCopy.notifications.count(notificationTemplates.length)}
                </div>
                <button className="btn btn-sm btn-outline" disabled={templateLoading} onClick={loadNotificationTemplates}>{settingsCopy.notifications.refresh}</button>
              </div>
              {!templateLoading && notificationTemplates.length === 0 && (
                <div style={{ color: '#777', fontSize: 13, padding: '12px 0' }}>{settingsCopy.notifications.empty}</div>
              )}
              {notificationTemplates.map(template => (
                <div key={template.key} style={{ borderTop: '1px solid #f0f0f0', padding: '16px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{template.key}</div>
                      <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                        {settingsCopy.notifications.variableList(template.variables)}
                      </div>
                    </div>
                    {template.overridden && <span style={{ fontSize: 12, color: '#166534', background: '#dcfce7', padding: '3px 8px', borderRadius: 999 }}>{settingsCopy.notifications.overridden}</span>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.notifications.zhTitle}</label>
                      <input
                        value={template.locales.zh.title}
                        onChange={e => updateNotificationTemplate(template.key, 'zh', 'title', e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '10px 0 6px' }}>{settingsCopy.notifications.zhContent}</label>
                      <textarea
                        value={template.locales.zh.content}
                        onChange={e => updateNotificationTemplate(template.key, 'zh', 'content', e.target.value)}
                        rows="3"
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{settingsCopy.notifications.enTitle}</label>
                      <input
                        value={template.locales.en.title}
                        onChange={e => updateNotificationTemplate(template.key, 'en', 'title', e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '10px 0 6px' }}>{settingsCopy.notifications.enContent}</label>
                      <textarea
                        value={template.locales.en.content}
                        onChange={e => updateNotificationTemplate(template.key, 'en', 'content', e.target.value)}
                        rows="3"
                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button className="btn btn-primary" disabled={templateLoading || notificationTemplates.length === 0} onClick={saveNotificationTemplates}>{settingsCopy.notifications.save}</button>
            </>
          )}

          {tab === 'system' && (
            <>
              <h3 style={{ marginBottom: 14 }}>{settingsCopy.system.title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{settingsCopy.system.maintenanceMode}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{settingsCopy.system.maintenanceModeDesc}</div>
                </div>
                <input type="checkbox" checked={config.maintenanceMode} onChange={e => update('maintenanceMode', e.target.checked)} />
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>{settingsCopy.system.backupTitle}</div>
                <button className="btn btn-outline" style={{ marginRight: 8 }} disabled={!!operating} onClick={() => runMaintenance('export')}>
                  {operating === 'export' ? settingsCopy.system.generating : settingsCopy.system.downloadDatabase}
                </button>
                <button className="btn btn-outline" style={{ marginRight: 8 }} disabled={!!operating} onClick={() => runMaintenance('logs')}>
                  {operating === 'logs' ? settingsCopy.system.exporting : settingsCopy.system.exportLogs}
                </button>
                <button className="btn btn-outline" disabled={!!operating} onClick={() => runMaintenance('cache')}>
                  {operating === 'cache' ? settingsCopy.system.processing : settingsCopy.system.clearCache}
                </button>
              </div>

              <div style={{ marginTop: 20, padding: 14, background: '#fff5f5', border: '1px solid #fee', borderRadius: 6 }}>
                <div style={{ fontWeight: 600, color: '#c33' }}>{settingsCopy.system.dangerTitle}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  {settingsCopy.system.dangerDescription}
                </div>
              </div>

              <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={save}>{settingsCopy.system.saveConfig}</button>
            </>
          )}
        </div>
      </div>}
    </div>
  );
}
